import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether the returned ref's element has (ever) entered the viewport (plus rootMargin
 * lookahead). Latches to true permanently on first intersection - used to trigger loading the
 * full-res image for a tile once it's visible or about to become visible, without ever swapping
 * back to the low-res placeholder again while scrolling away.
 */
export function useInView(rootMargin = '600px 0px') {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView];
}
